/* eslint sort-keys: "error" */
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { or } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';

import type { BannerStyle } from '../../types/components/prism/banner.ts';
import type { IconName } from '../../types/components/prism/icon.ts';
import styles from './banner.module.css';
import PrismButton from './button.gts';
import PrismCheckbox from './checkbox.gts';
import PrismIcon from './icon.gts';
import PrismLoadingSpinner from './loading-spinner.gts';

const bannerStyleToButtonStyle = {
  brand: 'brand',
  critical: 'critical',
  info: 'primary',
  intelligence: 'intelligence',
  success: 'success',
  warning: 'warning',
} as const;

type Action = {
  action: (event: MouseEvent) => unknown;
  disabled?: boolean;
  iconAfter?: IconName;
  iconBefore?: IconName;
  isLoading?: boolean;
  label: string;
};

type BannerPosition = 'bottom' | 'island' | 'top';
type ButtonType =
  (typeof bannerStyleToButtonStyle)[keyof typeof bannerStyleToButtonStyle];
type ClassForBannerPosition = `position-${BannerPosition}`;
type ClassForBannerStyle = BannerStyle;

interface PrismBannerSignature {
  Args: {
    bannerMessage: string;
    bannerStyle?: BannerStyle;
    bannerTitle?: string;
    checkbox?: {
      action: (event: MouseEvent) => unknown;
      label: string;
    };
    close?: (event: MouseEvent) => unknown;
    hasCta?: boolean;
    icon?: IconName;
    isLoading?: boolean;
    isOneLine?: boolean;
    link?: {
      action?: (event: MouseEvent) => unknown;
      icon?: boolean;
      text: string;
      url?: string;
    };
    position?: BannerPosition;
    primaryAction?: Action;
    secondaryAction?: Action;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class PrismBanner extends Component<PrismBannerSignature> {
  @tracked checked = false;

  get bannerStyle(): BannerStyle {
    return this.args.bannerStyle ?? 'info';
  }

  get buttonType(): ButtonType {
    return bannerStyleToButtonStyle[this.bannerStyle];
  }

  get classForBannerPosition(): ClassForBannerPosition {
    const position = this.args.position ?? 'island';

    return `position-${position}` as const;
  }

  get classForBannerStyle(): ClassForBannerStyle {
    return this.bannerStyle;
  }

  get hasCheckbox(): boolean {
    const { checkbox } = this.args;

    return Boolean(checkbox?.action) && Boolean(checkbox?.label);
  }

  get hasSecondaryAction(): boolean {
    const { checkbox, secondaryAction } = this.args;

    return !checkbox?.action && Boolean(secondaryAction?.label);
  }

  get isOneLine(): boolean {
    const { isOneLine } = this.args;

    return Boolean(isOneLine) && !this.hasCheckbox && !this.hasSecondaryAction;
  }

  @action
  close(event: MouseEvent): void {
    this.args.close?.(event);
  }

  @action
  linkAction(event: MouseEvent): void {
    this.args.link?.action?.(event);
  }

  @action
  primaryAction(event: MouseEvent): void {
    if (this.checked) {
      this.args.checkbox?.action(event);
    } else {
      this.args.primaryAction?.action(event);
    }
  }

  @action
  toggleChecked(): void {
    this.checked = !this.checked;
  }

  <template>
    <div
      class={{local
        styles
        "container"
        this.classForBannerStyle
        (unless this.isOneLine "cta-full-width")
        this.classForBannerPosition
      }}
      data-test-banner
      data-test-id="banner"
      ...attributes
    >
      <div class={{styles.inner-container}}>
        {{#if @isLoading}}
          <div class={{styles.icon}}>
            <PrismLoadingSpinner @color={{this.bannerStyle}} @size="medium" />
          </div>
        {{else if @icon}}
          <div class={{styles.icon}}>
            <PrismIcon
              @color={{this.bannerStyle}}
              @name={{@icon}}
              @size="medium"
            />
          </div>
        {{/if}}

        <div class={{styles.content}}>
          <div class={{styles.title-and-message}}>
            {{#if @bannerTitle}}
              <h4 class={{styles.title}}>
                {{@bannerTitle}}
              </h4>
            {{/if}}

            {{#if @bannerMessage}}
              <p class={{styles.message}}>
                {{@bannerMessage}}

                {{#if (or @link.url @link.action)}}
                  <a
                    class="prism-link prism-link--secondary"
                    href={{or @link.url ""}}
                    rel="noopener noreferrer"
                    target="_blank"
                    {{on "click" this.linkAction}}
                  >
                    {{@link.text}}

                    {{#if @link.icon}}
                      <PrismIcon
                        @color="inherit"
                        @name="link-out"
                        @size="small"
                      />
                    {{/if}}
                  </a>
                {{/if}}
              </p>
            {{/if}}
          </div>

          {{#if @hasCta}}
            <div class={{styles.ctas}} data-test-id="banner-cta-container">
              <PrismButton
                @buttonType={{this.buttonType}}
                @iconAfter={{@primaryAction.iconAfter}}
                @iconBefore={{@primaryAction.iconBefore}}
                @isDisabled={{@primaryAction.disabled}}
                @isLoading={{@primaryAction.isLoading}}
                @label={{@primaryAction.label}}
                @onClick={{this.primaryAction}}
                data-test-id="banner-primary-action"
              />

              {{#if this.hasSecondaryAction}}
                <PrismButton
                  @buttonType="secondary"
                  @iconAfter={{@secondaryAction.iconAfter}}
                  @iconBefore={{@secondaryAction.iconBefore}}
                  @isDisabled={{@secondaryAction.disabled}}
                  @isLoading={{@secondaryAction.isLoading}}
                  @label={{@secondaryAction.label}}
                  @onClick={{@secondaryAction.action}}
                  data-test-id="banner-secondary-action"
                />
              {{/if}}

              {{#if this.hasCheckbox}}
                <PrismCheckbox
                  @checked={{this.checked}}
                  @onChange={{this.toggleChecked}}
                  data-test-id="banner-checkbox"
                >
                  {{@checkbox.label}}
                </PrismCheckbox>
              {{/if}}
            </div>
          {{/if}}
        </div>

        {{#if @close}}
          <PrismButton
            @buttonType="ghost"
            @icon="cross"
            @isSmall={{true}}
            @onClick={{this.close}}
            class={{styles.close-button}}
            data-test-id="banner-close-btn"
          />
        {{/if}}
      </div>
    </div>
  </template>
}
